import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, Download, DollarSign, Info } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { useRevenue, deptLabel, type SaleRow } from "./api";

// ============================================================================
// Revenue & sales report — invoice totals from mirror_sales, sliced by period /
// department / salesman / zone. Money view for the owner. Same shape as the
// equipment report (presets, group-by, bar chart, table, CSV).
// ============================================================================

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1] as const;

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function todayISO(): string { return iso(new Date()); }
function yearsAgoISO(n: number): string { const d = new Date(); d.setFullYear(d.getFullYear() - n); return iso(d); }

type PresetKey = "this-year" | "last-year" | "last-12mo" | "last-3yr" | "custom";
function presetRange(key: PresetKey): { from: string; to: string } {
  const y = new Date().getFullYear();
  switch (key) {
    case "this-year": return { from: `${y}-01-01`, to: todayISO() };
    case "last-year": return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    case "last-12mo": return { from: yearsAgoISO(1), to: todayISO() };
    case "last-3yr": return { from: yearsAgoISO(3), to: todayISO() };
    default: return { from: `${y}-01-01`, to: todayISO() };
  }
}
const PRESETS: [PresetKey, string][] = [
  ["this-year", "This year"], ["last-year", "Last year"],
  ["last-12mo", "Last 12 months"], ["last-3yr", "Last 3 years"], ["custom", "Custom range"],
];

type GroupKey = "month" | "dept" | "salesman" | "zone";
const GROUPS: [GroupKey, string][] = [
  ["month", "Month"], ["dept", "Department"], ["salesman", "Salesman"], ["zone", "Zone (city)"],
];

function monthLabel(ym: string): string {
  const [y, m] = ym.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
}
function money(v: number): string {
  return v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function groupValue(r: SaleRow, by: GroupKey): string {
  switch (by) {
    case "month": return r.inv_date.slice(0, 7);
    case "dept": return deptLabel(r.dept);
    case "salesman": return (r.salesman ?? "").trim() || "(unassigned)";
    case "zone": return (r.city ?? "").trim() || "(unknown zone)";
  }
}

interface GroupRow { key: string; label: string; revenue: number; count: number }

function StyledSelect({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="h-9 cursor-pointer appearance-none rounded-lg border border-hairline bg-white pl-3 pr-9 text-sm font-medium text-slate-700 transition-colors duration-150 ease-out-quart hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30">
        {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
    </div>
  );
}

export function RevenueReport() {
  const [preset, setPreset] = useState<PresetKey>("this-year");
  const [from, setFrom] = useState(() => presetRange("this-year").from);
  const [to, setTo] = useState(() => presetRange("this-year").to);
  const [groupBy, setGroupBy] = useState<GroupKey>("dept");
  const { data, isLoading, isError } = useRevenue(from, to);

  function choosePreset(key: PresetKey) {
    setPreset(key);
    if (key !== "custom") { const r = presetRange(key); setFrom(r.from); setTo(r.to); }
  }

  const { groups, totalRev, totalCount } = useMemo(() => {
    const all = data?.rows ?? [];
    const map = new Map<string, GroupRow>();
    for (const r of all) {
      const key = groupValue(r, groupBy);
      const g = map.get(key) ?? { key, label: groupBy === "month" ? monthLabel(key) : key, revenue: 0, count: 0 };
      g.revenue += r.inv_amount; g.count += 1;
      map.set(key, g);
    }
    let groups = Array.from(map.values());
    groups = groupBy === "month" ? groups.sort((a, b) => a.key.localeCompare(b.key)) : groups.sort((a, b) => b.revenue - a.revenue);
    const totalRev = all.reduce((s, r) => s + r.inv_amount, 0);
    return { groups, totalRev, totalCount: all.length };
  }, [data, groupBy]);

  const max = groups.reduce((m, g) => Math.max(m, g.revenue), 0);
  const TOP = 15;
  const chartGroups = groupBy === "month" ? groups : groups.slice(0, TOP);

  function exportCsv() {
    const groupLabel = GROUPS.find(([k]) => k === groupBy)?.[1] ?? groupBy;
    const lines: string[] = [];
    lines.push("Sedgwick Heating — Revenue & sales");
    lines.push(`Date range,${from} to ${to}`);
    lines.push(`Grouped by,${groupLabel}`);
    lines.push(`Total revenue,${totalRev.toFixed(2)}`);
    lines.push(`Invoices,${totalCount}`);
    if (data?.truncated) lines.push("Note,Results capped — totals are a floor; narrow the range");
    lines.push("");
    lines.push(`${groupLabel},Revenue,Invoices,% of revenue`);
    for (const g of groups) {
      const pct = totalRev > 0 ? ((g.revenue / totalRev) * 100).toFixed(1) : "0.0";
      const safe = /[",\n]/.test(g.label) ? `"${g.label.replace(/"/g, '""')}"` : g.label;
      lines.push(`${safe},${g.revenue.toFixed(2)},${g.count},${pct}%`);
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `revenue_${groupBy}_${from}_${to}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  return (
    <div>
      <Card className="mb-5 p-4">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <Field label="Date range"><StyledSelect value={preset} onChange={(v) => choosePreset(v as PresetKey)} options={PRESETS} /></Field>
          <Field label="From"><input type="date" value={from} max={to} onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }} className="h-9 rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" /></Field>
          <Field label="To"><input type="date" value={to} min={from} max={todayISO()} onChange={(e) => { setTo(e.target.value); setPreset("custom"); }} className="h-9 rounded-lg border border-hairline bg-white px-3 text-sm text-slate-700 hover:border-slate-300 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" /></Field>
          <Field label="Group by"><StyledSelect value={groupBy} onChange={(v) => setGroupBy(v as GroupKey)} options={GROUPS} /></Field>
          <div className="ml-auto"><Button variant="secondary" size="sm" onClick={exportCsv} disabled={groups.length === 0}><Download className="h-4 w-4" /> Export CSV</Button></div>
        </div>
      </Card>

      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-sm text-slate-600">
          {!isLoading && !isError && (
            <>
              <span className="text-2xl font-bold tabular-nums text-slate-900">{money(totalRev)}</span>
              <span className="ml-2">across {totalCount.toLocaleString()} invoices · {groups.length} {GROUPS.find(([k]) => k === groupBy)?.[1].toLowerCase()} groups</span>
              <InfoTip className="ml-2" label="How this report is figured">Revenue is the invoice totals from ESC sales, by invoice date. ESC remains the accounting system of record; this is a read-only view.</InfoTip>
            </>
          )}
        </div>
        {data?.truncated && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700"><Info className="h-3.5 w-3.5" /> Results capped — narrow the range</span>}
      </div>

      {isError ? (
        <Card className="p-10 text-center"><p className="text-sm text-slate-600">Couldn't load revenue. Check the connection and try again.</p></Card>
      ) : isLoading ? (
        <Card className="p-10 text-center"><p className="text-sm text-slate-500">Loading revenue…</p></Card>
      ) : groups.length === 0 ? (
        <Card className="p-10 text-center"><DollarSign className="mx-auto h-10 w-10 text-slate-300" /><h3 className="mt-3 text-[15px] font-bold tracking-tight text-slate-900">No sales in this range</h3></Card>
      ) : (
        <>
          <Card className="mb-4 p-5">
            <h3 className="mb-4 text-[15px] font-bold tracking-tight text-slate-900">Revenue by {GROUPS.find(([k]) => k === groupBy)?.[1].toLowerCase()}{chartGroups.length < groups.length && <span className="font-normal text-slate-400"> · top {TOP}</span>}</h3>
            <div className="space-y-2.5">
              {chartGroups.map((g, i) => (
                <div key={g.key} className="flex items-center gap-3">
                  <div className="w-40 flex-shrink-0 truncate text-right text-xs font-medium text-slate-600" title={g.label}>{g.label}</div>
                  <div className="relative h-6 flex-1 overflow-hidden rounded-md bg-slate-100">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${max > 0 ? (g.revenue / max) * 100 : 0}%` }} transition={{ duration: 0.5, ease: EASE_OUT_EXPO, delay: Math.min(i * 0.02, 0.25) }} className="h-full rounded-md bg-emerald-500" />
                  </div>
                  <div className="w-28 flex-shrink-0 text-right text-xs tabular-nums"><span className="font-semibold text-slate-900">{money(g.revenue)}</span></div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-slate-50/60 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5">{GROUPS.find(([k]) => k === groupBy)?.[1]}</th>
                  <th className="px-4 py-2.5 text-right">Revenue</th>
                  <th className="px-4 py-2.5 text-right">Invoices</th>
                  <th className="px-4 py-2.5 text-right">Avg / invoice</th>
                  <th className="px-4 py-2.5 text-right">% of revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {groups.map((g) => (
                  <tr key={g.key} className="transition-colors hover:bg-emerald-50/40">
                    <td className="px-4 py-2.5 font-medium text-slate-800">{g.label}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-900">{money(g.revenue)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{g.count.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{money(g.count > 0 ? g.revenue / g.count : 0)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{((g.revenue / totalRev) * 100).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-hairline bg-slate-50/60 font-semibold">
                  <td className="px-4 py-2.5 text-slate-900">Total</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-900">{money(totalRev)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{totalCount.toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{money(totalCount > 0 ? totalRev / totalCount : 0)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">100%</td>
                </tr>
              </tfoot>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="flex flex-col gap-1"><span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>{children}</label>;
}
